import { useNotification } from "@context/NotificationContext";
import type { CreateDisputePayload } from "@services/disputes/types";
import { createDispute } from "@services/disputesApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";

const SERVICE = "bff-vessel-call";
const LANG_BASE = "vessel-call.operations";

type UseSubmitDisputeMutationOptions = {
  successTitleKey: "dispute-success-title" | "no-dispute-success-title";
  invalidateDetails?: boolean;
};

export const useSubmitDisputeMutation = ({
  successTitleKey,
  invalidateDetails = false,
}: UseSubmitDisputeMutationOptions) => {
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const mutation = useMutation<unknown, unknown, CreateDisputePayload>({
    mutationFn: (payload) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "Operations",
          action: payload.type === "NO_DISPUTE" ? "submit_no_dispute" : "submit_dispute",
          entityType: "dispute",
        },
        (transactionId) => createDispute({ service: SERVICE, payload, transactionId }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["disputes"] });

      if (invalidateDetails) {
        await queryClient.invalidateQueries({ queryKey: ["dispute"] });
      }

      await queryClient.invalidateQueries({ queryKey: ["eligible-associated-movements"] });
      await queryClient.invalidateQueries({ queryKey: ["vesselCall"] });

      notify({
        title: t(`${LANG_BASE}.${successTitleKey}`),
        type: "success",
      });
    },
    onError: () => {
      notify({
        title: t("common.error"),
        description: t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  return {
    submit: mutation.mutate,
    isSubmitting: mutation.isPending,
  };
};
